import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { CanalDeContacto, CarlineSelect, CodigoPostal, ConsentQuestion, Email, Loading, MultipleCTASelection, PantallaNombreApellido, Telefono } from 'src/components';
import { useIsComerciales } from 'src/feature-app/hooks';
import { ConfirmacionLeadsNoQuestions, LegalDatosPersonalesLeads, MapaLeads } from 'src/forms';
import { MapaLeadsInPage } from 'src/forms/Leads/InPage';
import { FormInfo, Steps } from 'src/types';
import { LeadGenericoController } from '../LeadGenericoController';
import { LeadsFormsProps } from '../LeadGenericoForm';
import { useInitializeLeadForm } from '../use-initialize-lead-form';
import { leadsVOPartialData } from './LeadsVOPartialData';
import { LayoutWrapper } from 'src/feature-app';

const getMapaFields = (inPage: boolean) => {
	if (inPage === true) {
		return <MapaLeadsInPage formName="vo" />;
	} else {
		return <MapaLeads formName="vo" />;
	}
};

export function VOForm({ updatedFormData, inPage, isConfigurador, isLeadDealer }: LeadsFormsProps) {
	const isComerciales = useIsComerciales();

	const [partialData, setPartialData] = useState(null);

	useEffect(() => {
		if (updatedFormData) {
			setPartialData(updatedFormData);
		} else {
			setPartialData(leadsVOPartialData(isConfigurador, isLeadDealer));
		}
	}, []);

	const CTAsKilometros = [
		{
			label: 'Hasta 25.000 Km',
			value: '25.000',
		},
		{
			label: 'Hasta 50.000 Km',
			value: '50.000',
		},
		{
			label: 'Hasta 100.000 Km',
			value: '100.000',
		},
		{
			label: 'Hasta 180.000 Km',
			value: '180.000',
		},
	];

	const CTAsAntiguedad = [
		{
			label: '0 a 2 años',
			value: '0-2',
		},
		{
			label: '2 a 5 años',
			value: '2-5',
		},
		{
			label: '5 a 8 años',
			value: '5-8',
		},
	];

	let index = 0;
	const defaultSteps: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿Qué modelo de ocasión </Text> te interesa?
				</>
			),
			fields: (
				<LayoutWrapper notALayer={inPage}>
					<CarlineSelect useCarline={true} isVO={true} showOtro={true} removeLabelNuevo={true} />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'Modelo',
			outputs: ['modelo'],
			outputsText: ['modelo'],
		},
		{
			title: (
				<>
					<Text bold>¿Cuántos kilómetros quieres </Text> que tenga como máximo el coche?
				</>
			),
			fields: (
				<LayoutWrapper notALayer={inPage}>
					<MultipleCTASelection ctaValues={CTAsKilometros} name="kilometros" nextOnClick />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'Kilometros',
			outputs: ['kilometros'],
			outputsText: ['kilometros'],
			ctas: CTAsKilometros,
		},
		{
			title: (
				<>
					<Text bold>¿Qué antigüedad </Text> te gustaría que tuviera el coche?
				</>
			),
			fields: (
				<LayoutWrapper notALayer={inPage}>
					<MultipleCTASelection ctaValues={CTAsAntiguedad} name="antiguedad" nextOnClick />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'Antiguedad',
			outputs: ['antiguedad'],
			outputsText: ['antiguedad'],
			ctas: CTAsAntiguedad,
		},
		isLeadDealer
			? null
			: {
					screenIndex: index++,
					name: 'Mapa',
					multiStep: true,
					component: getMapaFields(inPage),
			  },
		{
			title: (
				<>
					Para poder contactar contigo, necesitamos saber <Text bold>quién eres</Text>
					<LegalDatosPersonalesLeads />
				</>
			),
			fields: (
				<LayoutWrapper notALayer={inPage}>
					<PantallaNombreApellido isComerciales={isComerciales} />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: (
				<>
					También nos gustaría conocer <Text bold>tu código postal</Text>
				</>
			),
			fields: (
				<LayoutWrapper notALayer={inPage}>
					<CodigoPostal />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'CodigoPostal',
			outputs: ['codigoPostal'],
			outputsText: ['codigoPostal'],
		},
		{
			title: (
				<>
					<Text bold>¿Cómo prefieres </Text>que contactemos contigo?
					<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
				</>
			),
			fields: (
				<LayoutWrapper notALayer={inPage}>
					<CanalDeContacto />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'Contacto',
			outputs: ['contacto'],
			outputsText: ['contacto'],
		},
		{
			title: (
				<>
					Para ello necesitaremos <Text bold>tus datos de contacto</Text>
				</>
			),
			fields: (
				<LayoutWrapper notALayer={inPage}>
					<Telefono paginaConfirmacion={false} required={true} />
					<Email paginaConfirmacion={false} required={true} paddingTop="24px" />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'EmailTelefono',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', '/', 'email'],
		},
		{
			fields: <ConsentQuestion questionType="lssi" />,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" />,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <ConfirmacionLeadsNoQuestions />,
			isLastStep: true,
			hidePreviousStep: true,
			name: 'Confirmacion',
		},
	];
	const filteredSteps = defaultSteps.filter((step) => step !== null);

	const { loading, steps, firstStep, lastStep } = useInitializeLeadForm(filteredSteps, false, inPage, true);

	const citaVentaInfo: Partial<FormInfo> = {
		numberOfScreens: steps.length,
	};

	return loading ? <Loading /> : <LeadGenericoController steps={steps} leadData={partialData} leadInfo={citaVentaInfo} firstStep={firstStep} lastStep={lastStep} inPage={inPage} isConfigurador={isConfigurador} isVO={true} />;
}
